import type { GitHunk } from "gitpod-next-api/supervisor/v1/environmentopts_pb";
import type { PlainMessage } from "@bufbuild/protobuf";

export type PlainGitHunk = PlainMessage<GitHunk>;

export const LINE_MARKER_ADDED = "+";
export const LINE_MARKER_REMOVED = "-";
export const LINE_MARKER_UNCHANGED = " ";

/**
 * Extracts the new file content from diff hunks by combining added and unchanged lines.
 * Note: This only returns the lines covered by the hunks, not the complete file.
 * For files with limited changes, this provides a reasonable preview.
 */
export const extractNewContentFromHunks = (hunks: PlainGitHunk[]): string => {
    const lines: string[] = [];
    for (const hunk of hunks) {
        const hunkLines = hunk.body.split("\n");
        for (const line of hunkLines) {
            if (line === "") continue;
            const marker = line[0];
            // Include added lines and unchanged lines (skip removed lines)
            if (marker === LINE_MARKER_ADDED || marker === LINE_MARKER_UNCHANGED) {
                lines.push(line.slice(1));
            }
        }
    }
    return lines.join("\n");
};

/**
 * Converts GitHunk[] to a unified diff patch string that can be parsed by @pierre/diffs.
 * The patch format follows the standard unified diff format:
 * --- a/filename
 * +++ b/filename
 * @@ -oldStart,oldLines +newStart,newLines @@ optional section
 * context/added/removed lines...
 */
export const convertHunksToPatch = (hunks: PlainGitHunk[], filePath: string): string => {
    if (hunks.length === 0) {
        return "";
    }

    const lines: string[] = [`--- a/${filePath}`, `+++ b/${filePath}`];

    for (const hunk of hunks) {
        // Build hunk header: @@ -oldStart,oldLines +newStart,newLines @@ section
        const header = `@@ -${hunk.originalStartLine},${hunk.originalLines} +${hunk.newStartLine},${hunk.newLines} @@${hunk.section ? ` ${hunk.section}` : ""}`;
        lines.push(header);

        // Add hunk body lines (already prefixed with +/-/space)
        const bodyLines = hunk.body.split("\n");
        for (const line of bodyLines) {
            if (line !== "") {
                lines.push(line);
            }
        }
    }

    return lines.join("\n");
};

/**
 * Simple hash function for content comparison.
 * Uses a fast string hash algorithm (djb2).
 */
export const hashString = (str: string): string => {
    let hash = 5381;
    for (let i = 0; i < str.length; i++) {
        hash = (hash * 33) ^ str.charCodeAt(i);
    }
    return (hash >>> 0).toString(16);
};

/**
 * Extracts content for specific lines from hunks and computes a hash.
 * Used to detect if the code a comment refers to has changed.
 *
 * @param hunks - The diff hunks
 * @param startLine - Start line number (1-based, in the new file)
 * @param endLine - End line number (1-based, in the new file)
 * @param side - Which side of the diff ("additions" for new file, "deletions" for old file)
 * @returns Hash of the content for the specified lines, or null if lines not found
 */
export const computeContentHashForLines = (
    hunks: PlainGitHunk[],
    startLine: number,
    endLine: number,
    side: "additions" | "deletions" = "additions",
): string | null => {
    const lines: string[] = [];

    for (const hunk of hunks) {
        const hunkLines = hunk.body.split("\n");
        let currentNewLine = hunk.newStartLine;
        let currentOldLine = hunk.originalStartLine;

        for (const line of hunkLines) {
            if (line === "") continue;
            const marker = line[0];
            const content = line.slice(1);

            if (side === "additions") {
                // Track new file line numbers
                // Note: Removed lines don't affect new file line numbers
                if (marker === LINE_MARKER_ADDED || marker === LINE_MARKER_UNCHANGED) {
                    if (currentNewLine >= startLine && currentNewLine <= endLine) {
                        lines.push(content);
                    }
                    currentNewLine++;
                }
            } else {
                // Track old file line numbers
                // Note: Added lines don't affect old file line numbers
                if (marker === LINE_MARKER_REMOVED || marker === LINE_MARKER_UNCHANGED) {
                    if (currentOldLine >= startLine && currentOldLine <= endLine) {
                        lines.push(content);
                    }
                    currentOldLine++;
                }
            }
        }
    }

    if (lines.length === 0) {
        return null;
    }

    return hashString(lines.join("\n"));
};

/**
 * Computes a hash for specific lines from raw file content.
 * Used to detect if the code a comment refers to has changed.
 *
 * @param content - The file content as a string
 * @param startLine - Start line number (1-based)
 * @param endLine - End line number (1-based)
 * @returns Hash of the content for the specified lines, or null if lines not found
 */
export const computeContentHash = (content: string, startLine: number, endLine: number): string | null => {
    if (content === "") {
        return null;
    }

    const allLines = content.split("\n");
    const selectedLines = allLines.slice(startLine - 1, endLine);

    if (selectedLines.length === 0) {
        return null;
    }

    return hashString(selectedLines.join("\n"));
};

export const isMdxFile = (path: string): boolean => path.toLowerCase().endsWith(".mdx");

export const isMarkdownFile = (path: string): boolean => {
    const lowerPath = path.toLowerCase();
    return lowerPath.endsWith(".md") || lowerPath.endsWith(".markdown") || isMdxFile(path);
};

export const isSvgFile = (path: string): boolean => path.toLowerCase().endsWith(".svg");

/**
 * Represents a range of lines in a diff hunk.
 */
export type HunkLineRange = {
    startLine: number;
    endLine: number;
};

/**
 * Checks if a line number falls within any of the diff hunk ranges.
 *
 * @param lineNumber - The line number to check (1-based)
 * @param hunkRanges - Array of hunk line ranges
 * @returns true if the line is within a hunk, false otherwise
 */
export const isLineInHunk = (lineNumber: number, hunkRanges: HunkLineRange[]): boolean => {
    return hunkRanges.some((range) => lineNumber >= range.startLine && lineNumber <= range.endLine);
};
